/**
 * CSV bulk import tab for the admissions page.
 *
 * Drag-drop a CSV → POST /api/students/admissions/bulk-csv → render
 * inserted + per-row errors. The CSV template link reflects the columns
 * the server accepts.
 */
import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CloudArrowUp, CheckCircle, Warning, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATE = [
  "first_name,middle_name,last_name,dob,gender,aadhar,phone,email,address,city,state,pincode,parent_father_name,parent_mother_name,parent_phone,parent_email,parent_aadhar,is_single_parent,single_parent_type,height_cm,weight_kg,blood_group,apaar_id,category,mother_tongue,class_name,section,room_no,bed_no,hostel_id,school_id",
  "Aarav,,Sharma,2012-04-12,male,1111-2222-3333,9000099911,aarav@example.com,12 MG Road,Bengaluru,KA,560001,Father Sharma,Mother Sharma,9000099912,,8888-7777-6666,false,,140,38,B+,APAAR-1234567890,general,Hindi,8,A,,,,",
  "Sunita,,Devi,2014-08-22,female,2222-3333-4444,9000099921,,5 Park St,Kolkata,WB,700016,,Sunita Devi,9000099922,,,true,mother,135,32,O+,APAAR-9876543210,obc,Bengali,6,A,,,,",
].join("\n");

function downloadTemplate() {
  const blob = new Blob([TEMPLATE], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "admissions_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function ResultSummary({ summary }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="p-3 rounded-md bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]">
        <div className="text-[10px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">Total</div>
        <div className="font-display text-2xl">{summary?.total_rows ?? 0}</div>
      </div>
      <div className="p-3 rounded-md bg-[var(--vs-success)]/10 border border-[var(--vs-success)]/30">
        <div className="text-[10px] uppercase font-mono tracking-widest text-[var(--vs-success)]">OK</div>
        <div className="font-display text-2xl text-[var(--vs-success)]" data-testid="csv-ok-count">{summary?.ok ?? 0}</div>
      </div>
      <div className="p-3 rounded-md bg-[var(--vs-danger)]/10 border border-[var(--vs-danger)]/30">
        <div className="text-[10px] uppercase font-mono tracking-widest text-[var(--vs-danger)]">Failed</div>
        <div className="font-display text-2xl text-[var(--vs-danger)]" data-testid="csv-failed-count">{summary?.failed ?? 0}</div>
      </div>
    </div>
  );
}

function InsertedList({ inserted }) {
  if (!inserted || inserted.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase font-mono tracking-widest text-[var(--vs-success)] mt-2 mb-1.5 flex items-center gap-1.5">
        <CheckCircle size={11} /> Inserted
      </div>
      <ul className="text-xs space-y-1">
        {inserted.map((r) => (
          <li key={r.vs_id} className="font-mono">Row {r.row} → {r.vs_id} · {r.name}</li>
        ))}
      </ul>
    </div>
  );
}

function ErrorsList({ errors }) {
  if (!errors || errors.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase font-mono tracking-widest text-[var(--vs-danger)] mt-2 mb-1.5 flex items-center gap-1.5">
        <Warning size={11} /> Errors
      </div>
      <ul className="text-xs space-y-2" data-testid="csv-errors">
        {errors.map((e) => (
          <li key={`row-${e.row}-${(e.error || "").slice(0, 24)}`} className="p-2 rounded border border-[var(--vs-danger)]/30 bg-[var(--vs-danger)]/5">
            <div className="font-mono">Row {e.row}: <span className="text-[var(--vs-danger)]">{e.error}</span></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CsvImportTab({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Pick a .csv file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/students/admissions/bulk-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      const ok = res.data?.summary?.ok || 0;
      const failed = res.data?.summary?.failed || 0;
      if (ok > 0) {
        toast.success(`Imported ${ok} student${ok !== 1 ? "s" : ""}${failed ? ` — ${failed} failed` : ""}`);
      } else if (failed > 0) {
        toast.error(`No rows imported — ${failed} validation errors`);
      }
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <div className="vs-card p-6 space-y-3">
        <div className="eyebrow">Bulk import — CSV</div>
        <div className="text-xs text-[var(--vs-text-secondary)] flex flex-wrap items-center gap-1">
          <span>Drop a UTF-8 CSV (max 5MB). Server validates row-by-row and reports errors. Inserted students start as</span>
          <Badge variant="outline" className="text-[10px]">DRAFT</Badge>
          <span>until medical certs are uploaded.</span>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="csv-template-download">
          <DownloadSimple size={12} className="mr-1.5" /> Download template
        </Button>
        <label className="mt-2 flex items-center justify-center gap-2 px-4 py-8 rounded-md border-2 border-dashed border-[var(--vs-border-focus)]/40 hover:border-[var(--vs-secondary)] cursor-pointer transition-colors">
          <CloudArrowUp size={20} weight="duotone" />
          <span className="text-sm">{busy ? "Uploading…" : "Choose CSV"}</span>
          <input
            data-testid="csv-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>

      <div className="vs-card p-6 space-y-3 max-h-[480px] overflow-y-auto">
        <div className="eyebrow">Import result</div>
        {!result && <p className="text-sm text-[var(--vs-text-secondary)]">Upload a CSV to see the per-row report here.</p>}
        {result && (
          <>
            <ResultSummary summary={result.summary} />
            <InsertedList inserted={result.inserted} />
            <ErrorsList errors={result.errors} />
          </>
        )}
      </div>
    </div>
  );
}
